import { supabase } from '@/integrations/supabase/client';

/**
 * Serviço centralizado para gerenciar URLs de imagens
 * Migra de servidor HTTP externo para Supabase Storage
 */
export class ImageUrlService {
  // Buckets configurados no Supabase
  private static readonly BUCKETS = {
    AVATARS: 'avatars',
    PROVIDER_AVATARS: 'provider-avatars',
    SERVICOS: 'servicos',
    DOCUMENTOS: 'documentos',
    LOGOS: 'logos',
    BANNERS: 'banners',
    GALERIA: 'galeria',
    CERTIFICADOS: 'certificados'
  } as const;

  // URL base do servidor antigo (para migração)
  private static readonly LEGACY_BASE_URL = 'http://213.199.44.151:4000/uploads/';

  /**
   * Converte URL do servidor antigo para Supabase Storage
   */
  static migrateLegacyUrl(legacyUrl: string): string {
    if (!legacyUrl) return legacyUrl;
    
    // Se já é uma URL do Supabase, retorna como está
    if (legacyUrl.includes('supabase.co/storage/')) {
      return legacyUrl;
    }

    // Se é uma URL do servidor antigo, tenta migrar
    if (legacyUrl.includes(this.LEGACY_BASE_URL)) {
      const filename = legacyUrl.replace(this.LEGACY_BASE_URL, '');
      
      // Tenta encontrar o arquivo nos buckets do Supabase
      // Por padrão, assume que é um avatar
      return this.getPublicUrl(this.BUCKETS.AVATARS, filename);
    }

    // Se é uma URL HTTPS do servidor antigo, também migra
    if (legacyUrl.includes('https://213.199.44.151:4000/uploads/')) {
      const filename = legacyUrl.replace('https://213.199.44.151:4000/uploads/', '');
      return this.getPublicUrl(this.BUCKETS.AVATARS, filename);
    }

    // Se é uma URL do domínio tamoaki.co.ao, mantém como está
    if (legacyUrl.includes('tamoaki.co.ao/')) {
      return legacyUrl;
    }

    // Para outras URLs, retorna como está
    return legacyUrl;
  }

  /**
   * Obtém URL pública do Supabase Storage
   */
  static getPublicUrl(bucket: string, path: string): string {
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  }

  /**
   * Obtém URL de avatar (migra automaticamente se necessário)
   */
  static getAvatarUrl(url: string | null | undefined): string {
    if (!url) return '';
    return this.migrateLegacyUrl(url);
  }

  /**
   * Obtém URL de imagem de serviço (migra automaticamente se necessário)
   */
  static getServiceImageUrl(url: string | null | undefined): string {
    if (!url) return '';
    return this.migrateLegacyUrl(url);
  }

  /**
   * Obtém URL de logo (migra automaticamente se necessário)
   */
  static getLogoUrl(url: string | null | undefined): string {
    if (!url) return '';
    return this.migrateLegacyUrl(url);
  }

  /**
   * Verifica se uma URL é válida (Supabase, domínio próprio ou migrada)
   */
  static isValidImageUrl(url: string): boolean {
    if (!url) return false;
    
    return (
      url.includes('supabase.co/storage/') ||
      url.includes('tamoaki.co.ao/') ||
      url.startsWith('data:image/') ||
      url.startsWith('blob:')
    );
  }

  /**
   * Obtém URL de fallback para avatar
   */
  static getFallbackAvatarUrl(): string {
    return this.getPublicUrl(this.BUCKETS.AVATARS, 'default-avatar.png');
  }

  /**
   * Obtém URL de fallback para imagem de serviço
   */
  static getFallbackServiceImageUrl(): string {
    return this.getPublicUrl(this.BUCKETS.SERVICOS, 'default-service.png');
  }

  /**
   * Lista todos os buckets disponíveis
   */
  static getAvailableBuckets(): string[] {
    return Object.values(this.BUCKETS);
  }

  /**
   * Verifica se um bucket existe
   */
  static async bucketExists(bucket: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.storage.from(bucket).list('', { limit: 1 });
      return !error;
    } catch {
      return false;
    }
  }
}

export default ImageUrlService;
